import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SoftButton } from '@/ui/design-system'

const List = ({ items }) => {
  if (!items?.length) return null
  return (
    <ul className="tamarra-done-list">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  )
}

const Kv = ({ rows }) => {
  if (!rows?.length) return null
  return (
    <div className="tamarra-done-kv-grid">
      {rows.map(([k, v], i) => (
        <span key={`${k}-${i}`} className="contents">
          <span>{k}</span>
          <b>{v}</b>
        </span>
      ))}
    </div>
  )
}

/**
 * Confirmation Tamarra (StepDone) — layout riche + API legacy (title/lines/note).
 */
const TamarraConfirmationStep = ({
  title,
  reference,
  lines = [],
  note,
  status = null,
  onDownloadVoucher = null,
  downloadLabel = null,
  tripTitle = null,
  tripLines = null,
  travelersTitle = null,
  travelers = null,
  amountsTitle = null,
  amountLines = null,
  nextTitle = null,
  nextSteps = null,
  demoBanner = null,
}) => {
  const { t } = useTranslation()
  const [dlMsg, setDlMsg] = useState('')

  const leftTrip = tripLines?.length ? tripLines : lines
  const showRich = Boolean(
    leftTrip?.length ||
      travelers?.length ||
      amountLines?.length ||
      nextSteps?.length ||
      onDownloadVoucher
  )

  const handleDownload = async () => {
    if (!onDownloadVoucher) return
    setDlMsg('')
    try {
      const msg = await onDownloadVoucher()
      if (msg) setDlMsg(String(msg))
    } catch {
      setDlMsg(
        t('tamarraBooking.done.downloadError', {
          defaultValue: 'Le téléchargement a échoué. Réessayez dans un instant.',
        })
      )
    }
  }

  return (
    <div className="tamarra-done-wrap">
      <div className="tamarra-done">
        <div className="tamarra-done-mark" aria-hidden>
          <svg width="30" height="30" viewBox="0 0 30 30">
            <path
              d="M7 15.5l5.5 5.5L23 10"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <span className="tamarra-label">
          {t('tamarraBooking.done.badge', {
            defaultValue: 'Réservation enregistrée',
          })}
        </span>
        <h3 id="tamarra-done-h" tabIndex={-1}>
          {title}
        </h3>
        {reference || status ? (
          <div className="tamarra-done-row">
            {reference ? <span className="tamarra-ref">{reference}</span> : null}
            {status?.txt ? (
              <span className={`tamarra-chip ${status.cls || 'ok'}`}>
                <span className="tamarra-chip-dot" aria-hidden />
                {status.txt}
              </span>
            ) : null}
          </div>
        ) : null}
        {onDownloadVoucher ? (
          <div className="tamarra-done-act">
            <SoftButton type="button" variant="solid" onClick={handleDownload}>
              {downloadLabel ||
                t('tamarraBooking.done.downloadVoucher', {
                  defaultValue: 'Télécharger le voucher (PDF)',
                })}
            </SoftButton>
            {dlMsg ? (
              <span className="tamarra-bk-rate" role="status">
                {dlMsg}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {showRich ? (
        <div className="tamarra-done-grid">
          <div className="tamarra-done-info">
            <h4>
              {tripTitle ||
                t('tamarraBooking.done.yourTrip', { defaultValue: 'Votre voyage' })}
            </h4>
            <Kv rows={leftTrip} />
            {travelers?.length ? (
              <>
                <h4>
                  {travelersTitle ||
                    t('tamarraBooking.done.travelers', { defaultValue: 'Voyageurs' })}
                </h4>
                <List items={travelers} />
              </>
            ) : null}
          </div>
          <div className="tamarra-done-info">
            {amountLines?.length ? (
              <>
                <h4>
                  {amountsTitle ||
                    t('tamarraBooking.done.amounts', { defaultValue: 'Montants' })}
                </h4>
                <Kv rows={amountLines} />
              </>
            ) : null}
            {nextSteps?.length ? (
              <>
                <h4>
                  {nextTitle ||
                    t('tamarraBooking.done.nextSteps', {
                      defaultValue: 'Prochaines étapes',
                    })}
                </h4>
                <List items={nextSteps} />
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      {demoBanner ? (
        <div className="tamarra-done-banner" role="note">
          {demoBanner}
        </div>
      ) : null}

      {note && !showRich ? <p className="tamarra-done-note">{note}</p> : null}
      {note && showRich ? (
        <p className="tamarra-done-note tamarra-done-note-wide">{note}</p>
      ) : null}
    </div>
  )
}

export default TamarraConfirmationStep
